import _ from "lodash";
import store from "store";
import { backendDateFormat, MMM_D_YYYY_HH_MM } from "constant/dates";
import axios from "axios";
import getCroppedImg from "components/common/image-crop/crop-image";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { durationOptions } from "../../constant";
import { AZURE_STORAGE_PREFIX } from "../../constant/env-variables";
import Joi from "joi";


dayjs.extend(utc);
dayjs.extend(timezone);

export const capitalize = (s) => {
  if (typeof s !== "string") return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const capitalizeEachWord = (inputString) => {
  const words = inputString.split(/\b/);

  for (let i = 0; i < words.length; i++) {
    if (/^[a-zA-Z0-9]+$/.test(words[i])) {
      words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1).toLowerCase();
    }
  }

  return words.join("");
};

/**
 * Formats a column name for display: replaces underscores with spaces and capitalizes each word.
 * Used for UI display only - does not modify stored data.
 * @example formatColumnName('short_description') => 'Short Description'
 */
export const formatColumnName = (str) => {
  if (str == null || typeof str !== "string") return str;
  return capitalizeEachWord(str.replace(/_/g, " "));
};

export const camelize = (str) =>
  str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (index === 0 ? word.toLowerCase() : word.toUpperCase()))
    .replace(/\s+/g, "");

export function chunkArray(myArray, chunkSize) {
  const results = [];

  while (myArray.length) {
    results.push(myArray.splice(0, chunkSize));
  }

  return results;
}

export function getChargeAmount(initialAmount) {
  const newInitialAmount = roundToDecimalPlaces(initialAmount / 100, 2);
  const date = new Date();
  const daysInCurrentMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const oneDayCost = newInitialAmount / daysInCurrentMonth;
  const spent = roundToDecimalPlaces(oneDayCost * date.getDate(), 2);
  return roundToDecimalPlaces(newInitialAmount - spent, 2) * 100;
}

export function toNormalPrice(price) {
  return roundToDecimalPlacesString(price / 100, 2);
}

export function getBodyTagFromHtml(html) {
  const bodyTag = html.match(/\<body[^>]*\>([^]*)\<\/body/m);
  return bodyTag ? bodyTag[1] : "";
}

export function getNameFirstLetters(firstName, lastName) {
  const fL = firstName ? firstName.charAt(0) : "";
  const lL = lastName ? lastName.charAt(0) : "";

  return `${fL}${lL}`;
}

export function formatDate(d, inFormat = backendDateFormat, outFormat = MMM_D_YYYY_HH_MM) {
  return dayjs.utc(d, inFormat).format(outFormat);
}

export function formatDateWithUserTimezone(d, timezone, outFormat = MMM_D_YYYY_HH_MM, inFormat = backendDateFormat) {
  let tz = timezone;
  if (!tz) {
    tz = store.getState().userData?.user?.timezone;
  }
  return dayjs.utc(d, inFormat).tz(tz).format(outFormat);
}

export function formatDateWithServerTimezone(d, outFormat = backendDateFormat) {
  return dayjs(d).utc().format(outFormat);
}

export function formatPhoneNumber(phoneNumberString) {
  const cleaned = `${phoneNumberString}`.replace(/\D/g, "");
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] || cleaned.length === 10 ? "+1 " : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  }
  return phoneNumberString;
}

  export function formatPhoneNumberByCountry(phoneNumberString, country = "us") {
    if (!phoneNumberString) return "";
  
    const digitsOnly = phoneNumberString.replace(/\D/g, "");
    const normalizedCountry = String(country).toLowerCase();
    const isUSorCA = normalizedCountry === "us" || normalizedCountry === "ca";
  
    if (isUSorCA && !phoneNumberString.startsWith("+1") && digitsOnly.length === 10) {
      return `+1${digitsOnly}`;
    }
  
    return digitsOnly;
  }  

export function textFromHtml(html) {
  return html.replace(/&nbsp;|<[^>]*>?/gim, "");
}

export function bytesToSize(bytes, decimals = 2) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

export function copyToClipboard(copyMe) {
  try {
    const elem = document.createElement("textarea");
    elem.setAttribute(
      "style",
      `
                position: absolute;
                z-index: -99999999;
                top: -10000px;
                left: -10000px;
                opacity: 0;
            `
    );
    document.body.appendChild(elem);
    elem.value = copyMe;
    elem.select();
    document.execCommand("copy");
    document.body.removeChild(elem);
  } catch (err) {
    console.log(err);
  }
}

export function nFormatter(num, digits = 1) {
  const si = [
    {
      value: 1,
      symbol: ""
    },
    {
      value: 1e3,
      symbol: "K"
    },
    {
      value: 1e6,
      symbol: "M"
    },
    {
      value: 1e9,
      symbol: "G"
    },
    {
      value: 1e12,
      symbol: "T"
    },
    {
      value: 1e15,
      symbol: "P"
    },
    {
      value: 1e18,
      symbol: "E"
    }
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  let i;
  for (i = si.length - 1; i > 0; i--) {
    if (num >= si[i].value) {
      break;
    }
  }
  return (num / si[i].value).toFixed(digits).replace(rx, "$1") + si[i].symbol;
}

export function numberWithCommas(number) {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Rounds a numeric value to a fixed number of decimal places using Math.round
 * (same semantics as {@link toCurrency} formatting).
 * @param {number|string} value
 * @param {number} [decimals=2]
 * @returns {number} Rounded number, or NaN if input is not a finite number
 */
export function roundToDecimalPlaces(value, decimals = 2) {
  const num = parseFloat(value);
  if (!Number.isFinite(num)) return NaN;
  const factor = 10 ** decimals;
  return Math.round((num + Number.EPSILON) * factor) / factor;
}

/**
 * Numeric rounding for form fields / APIs: same as {@link roundToDecimalPlaces}, but returns `0` when the value is not finite (avoids NaN in controlled inputs and Joi `number()`).
 */
export function roundToDecimalPlacesSafe(value, decimals = 2) {
  const n = roundToDecimalPlaces(value, decimals);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Same as {@link roundToDecimalPlaces} but returns a fixed-width decimal string (e.g. "12.34").
 * Prefer {@link roundToDecimalPlaces} or {@link roundToDecimalPlacesSafe} for values bound to Joi `number()` or `type="number"` inputs.
 */
export function roundToDecimalPlacesString(value, decimals = 2) {
  const rounded = roundToDecimalPlaces(value, decimals);
  if (!Number.isFinite(rounded)) {
    return parseFloat(value).toFixed(decimals);
  }
  return rounded.toFixed(decimals);
}

export function toCurrency(value, symbol = "$", decimals = 2) {
  if (decimals === 0) {
    return `${symbol}${Math.round(value)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  }
  const rounded = roundToDecimalPlaces(value, decimals);
  const forFormat = Number.isFinite(rounded) ? rounded : parseFloat(value);
  return `${symbol}${forFormat
    .toFixed(decimals)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
}

export function calculatePermissionStatus(sumOfPermissions, permissionValue) {
  // eslint-disable-next-line no-bitwise
  return !!(sumOfPermissions & permissionValue);
}

export function parsePhoneForBack(str) {
  return str && str.replace(/\D/g, "");
}

export function uniquifyArrayOfObjects(array, propertyName) {
  return array.filter((thing, index, self) => index === self.findIndex((t) => t[propertyName] === thing[propertyName]));
}

export function arrayMove(arr, oldIndex, newIndex) {
  if (newIndex >= arr.length) {
    let k = newIndex - arr.length + 1;
    while (k--) {
      arr.push(undefined);
    }
  }
  arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
  return arr;
}

export function isValidUrl(url) {
  const urlExp = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/;
  const urlExpRegex = new RegExp(urlExp);
  return url?.match(urlExpRegex);
}

export function generateValidUrl(url) {
  const withHttpExp =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
  const withHttpExpRegex = new RegExp(withHttpExp);
  return url?.match(withHttpExpRegex) ? url : `https://${url}`;
}

export function generateSubdomainBaseUrl(baseUrl, noSubdomainApis, apiUrl) {
  if (apiUrl && noSubdomainApis.some((api) => apiUrl.includes(api))) {
    return baseUrl;
  }

  const { host } = window.location;

  if (host.includes("localhost")) {
    const dotSplintedHost = host.split(".");
    const subdomain = dotSplintedHost[0];
    return baseUrl.replace("api", subdomain);
  }
  const dotSplintedHost = host.split(".");
  if (dotSplintedHost.length <= 2) {
    return baseUrl.replace("api.", "");
  }
  return baseUrl.replace("api", dotSplintedHost[0]);
}

export function paramParserForPagination(params) {
  if (params) {
    const { skip, limit, sortInfo, groupBy, filterValue, uiViewTypeId } = params;

    /** filterValue example **/
    // [
    // name: "Company Name"
    // operator: "contains"
    // type: "string"
    // value: ""
    // ]

    /** sortInfo example **/
    // dir: 1
    // id: "Address 1"
    // name: "Address 1"
    // type: undefined

    /** groupBy example **/
    // ['Title']
    return {
      current_page: (limit > 0 ? parseInt(skip / limit, 10) + 1 : 1) || 1,
      page_size: limit,
      app_ui_view_type_id: uiViewTypeId,
      ...(filterValue && {
        filter_fields: filterValue
          .filter(({ value }) => value !== "" && value !== null)
          .map(({ name, operator, value }) => ({
            name,
            operator,
            value: value && typeof value === "string" ? value?.split("---").join(",") : value
          }))
      }),
      ...(sortInfo && sortInfo.name && {
        sorter_fields: {
          name: sortInfo.name,
          dir: sortInfo.dir
        }
      }),
      ...(groupBy && {
        group_by: groupBy
      })
    };
  }

  return null;
}

export async function downloadFile(data, fileName) {
  const URL = window.URL || window.webkitURL;
  const url = URL.createObjectURL(new Blob([data], { type: "text/html" }));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// Remove tabindex from modal to make tinymce dropdown work
export function fixTabindexInModals() {
  const muiDialogContainer = document.getElementsByClassName("MuiDialog-container");
  Array.from(muiDialogContainer).forEach((el) => {
    el.removeAttribute("tabIndex");
  });
}

export function isArrayEqual(x, y) {
  return _(x).differenceWith(y, _.isEqual).isEmpty();
}

export function getObjectsDifferences(a, b) {
  return _.reduce(a, (result, value, key) => (_.isEqual(value, b[key]) ? result : result.concat(key)), []);
}

export function JSONParse(str) {
  let parsedData = {};
  try {
    parsedData = JSON.parse(str);
  } catch (e) {
    return {};
  }

  return parsedData;
}

export function parseValue(obj, val) {
  let parsedData = {};
  const str = Object.values(obj).find((i) => i.includes(val));
  try {
    if (str) parsedData = JSON.parse(str);

    parsedData = {};
  } catch (e) {
    return {};
  }
  return parsedData;
}

export const getCurrentPathname = (pathname) => {
  let currentPathname = "";
  const url = pathname.split("/");
  switch (url[2]) {
    case "companies":
      currentPathname = "category/companies-1ca6gzw/";
      break;
    case "contacts":
      currentPathname = "category/contacts-1umab57";
      break;
    case "dashboard":
      currentPathname = "category/dashboard-tspn1b/";
      break;
    case "documents":
      currentPathname = "category/documentsproposals-1j87jpr/";
      break;
    case "opportunities":
      currentPathname = "category/opportunities-1xj0y7f/";
      break;
    case "products-services":
      currentPathname = "category/productsservices-1go3dg/";
      break;
    case "settings":
      currentPathname = "category/settings-1o9z6ky/";
      break;
    default:
      currentPathname = "";
  }
  return currentPathname;
};

export const getIntegrationValidation = (tenantActiveIntegrations, integrations) => {
  return tenantActiveIntegrations?.reduce((acc, current) => {
    const integration = integrations.find((integration) => integration.integration_id === current.integration_id);
    if (integration.validation) {
      acc.push(integration.validation);
      return acc;
    }
    return acc;
  }, []);
};


function buildJoiField(rules, baseSchema = Joi.string()) {
  let schema = baseSchema;

  for (const [rule, value] of Object.entries(rules)) {
    if (typeof schema[rule] === "function") {
      schema = value === true
        ? schema[rule]()
        : schema[rule](value);
    }
  }

  return schema;
}


export const extendSchema = (baseSchema, config) => {
  if (!baseSchema || !config) return baseSchema;

  const additions = {};

  for (const [key, rules] of Object.entries(config)) {
    let baseField;

    if (typeof baseSchema.extract === "function") {
      try {
        baseField = baseSchema.extract(key);
      } catch {
        baseField = undefined;
      }
    }

    if (!baseField) {
      baseField = Joi.any();
    }

    additions[key] = buildJoiField(rules, baseField);
  }

  if (Object.keys(additions).length > 0 && typeof baseSchema.keys === "function") {
    return baseSchema.keys(additions);
  }

  return baseSchema;
};





/**
 * @description Deletes object keys from provided argument
 * @param {Object<any>} obj
 * @param {string[]} keys
 */
export const delKeys = (obj = {}, keys = []) => {
  for (const key of keys) delete obj[key];
};

export const assign = (obj = {}, props) => Object.assign(obj, props);

export const getImage = async (photoUrl) => {
  try {
    const resp = await axios.get(photoUrl, { responseType: "blob" });
    if (resp) {
      const imageBlob = resp.data;

      if (imageBlob) {
        const imageSrc = {
          file: imageBlob,
          name: `${Math.random().toString(36).substring(2, 9)}.jpeg`
        };

        if (imageBlob.type !== "image/jpeg") {
          const area = {
            height: 200,
            width: 200,
            x: 0,
            y: 0
          };
          const imageBlob = await getCroppedImg(photoUrl, area, 0);
          imageSrc.file = imageBlob;
        }

        return { src: imageSrc };
      }
    }
  } catch (e) {
    console.error(e);
  }
};

export const getSocialMediaItems = ({ facebook_url, linkedin_url, twitter_url }, socialMediasList) => {
  const socialMedias = [];

  const socialMediaUrls = {
    Facebook: facebook_url,
    LinkedIn: linkedin_url,
    Twitter: twitter_url
  };

  for (const [platform, url] of Object.entries(socialMediaUrls)) {
    if (url) {
      const foundSM = socialMediasList.find((item) => item.name === platform);
      const socialMediaItem = {
        social_media_id: foundSM.social_media_id,
        social_media: foundSM.name,
        url
      };
      socialMedias.push(socialMediaItem);
    }
  }

  return socialMedias;
};

export const getQueryParams = () =>
  new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop)
  });

/**
 * Determine if an image has changed compared to its initial URL.
 *
 * - No change: both empty, or both non-empty and same URL.
 * - Changed:
 *   - had image, now none (cleared)
 *   - none before, image now
 *   - image URL changed
 */
export function hasImageChanged(initialUrl, image) {
  const initial = initialUrl || "";
  const currentUrl = image?.url || "";

  const initialHadImage = Boolean(initial);
  const currentHasImage = Boolean(currentUrl);

  if (!initialHadImage && !currentHasImage) return false;
  if (initialHadImage && !currentHasImage) return true;
  if (!initialHadImage && currentHasImage) return true;

  return initial !== currentUrl;
}

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const extractMergeFields = (str) => {
  const regex = /\[([^\]]+?)\]/g;
  const mergeFields = str.match(regex) || [];

  // remove the brackets and return a new array with mergeFields
  return mergeFields.map((field) => field.replace(/\[|\]/g, ""));
};

export const replaceMergeFields = (string, mergeFields, tenantId, tenantUserId) => {
  if (mergeFields) {
    // eslint-disable-next-line prefer-const
    for (let [key, value] of Object.entries(mergeFields)) {
      if (value !== undefined) {
        const regex = new RegExp(`\\[${key}\\]`, "g");
        if (key === "Organization.Rep Photo" || key === "Organization.Logo") {
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = string;

          const imageElements = Array.from(tempDiv.querySelectorAll(`.image-token img[alt="[${key}]"]`));

          imageElements.forEach((img) => {
            img.src =
              key === "Organization.Logo"
                ? `${AZURE_STORAGE_PREFIX}/${tenantId}/companyLogo/${value}`
                : `${AZURE_STORAGE_PREFIX}/${tenantId}/${tenantUserId}/${value}`;
            img.style.opacity = 1;
            img.style.border = "none";
            img.alt = key;
          });

          string = tempDiv.innerHTML;
          tempDiv.remove();
        } else {
          if (key.toLowerCase().includes("phone") || key.toLowerCase().includes("fax")) {
            value = formatPhoneNumber(value);
          }
          string = string?.replace(regex, value ? `<span style="display: none;">{${key}}</span>${value}` : `[${key}]`);
        }
      }
    }
  }
  return string;
};
export const replaceMergeFieldsWithoutHiddenMergeField = (string, mergeFields) => {
  if (mergeFields) {
    for (const [key, value] of Object.entries(mergeFields)) {
      if (value !== undefined) {
        const regex = new RegExp(`\\[${key}\\]`, "g");
        string = string?.replace(regex, value ? `<span style="display: none;">{${key}}</span> ${value}` : `[${key}]`);
      }
    }
    return string;
  }
};

export const getMergeFieldsFromHtml = (html) => {
  const regExp = /{([^}]+)}/g;
  const arrayOfMergeFields = html.match(regExp) || [];
  return arrayOfMergeFields.map((item) => item.replace("{", "").replace("}", ""));
};

export const getMergeFieldsFromHtmlSquareBrackets = (html) => {
  const regExp = /\[(.*?)\]/g;
  const arrayOfMergeFields = html?.match(regExp) || [];
  return arrayOfMergeFields.map((item) => item.replace("[", "").replace("]", ""));
};

export const convertToMergeFields = (htmlString, mergeValues) => {
  if (htmlString) {
    const replacedMergeFields = getMergeFieldsFromHtml(htmlString);
    replacedMergeFields.forEach((item) => {
      const mergeFieldIndex = htmlString.indexOf(`{${item}}`);
      const valueIndex = htmlString.indexOf(mergeValues[item]);
      if (valueIndex > mergeFieldIndex) {
        // <span style="display: none;">{Organization.User First Name}</span> Edward
        htmlString = htmlString.replace(
          `<span style="display: none;">{${item}}</span> ${mergeValues[item]}`,
          `[${item}]`
        );
      }
    });
    return { htmlString, replacedMergeFields };
  }
  return { htmlString: "", replacedMergeFields: [] };
};

export const decodeHtml = (html) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value.trim();
};

export const getDurationOption = (endDate, startDate) => {
  const endDateMin = dayjs(endDate).startOf("minute");
  const dueDateMin = dayjs(startDate).startOf("minute");
  const differenceInMinutes = endDateMin.diff(dueDateMin, "minute");
  const foundOption = durationOptions.find((item) => item.code === differenceInMinutes)?.code;
  return foundOption || "custom";
};

export function getBase64ImageFromUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

export function getMaxNodeDepth(nodes) {
  const nodeMap = new Map();
  nodes.forEach((node) => nodeMap.set(node.node_id, node));

  function getDepth(nodeId, checked = new Set()) {
    if (!nodeMap.has(nodeId) || checked.has(nodeId)) return { vertical: 0, horizontal: 0 };

    checked.add(nodeId);
    const node = nodeMap.get(nodeId);

    if (!node.connector || node.connector.length === 0) {
      return { vertical: 1, horizontal: 0 };
    }

    let maxVertical = 1;
    let maxHorizontal = node.connector.length;

    node.connector.forEach((conn) => {
      const { vertical, horizontal } = getDepth(conn, new Set(checked));
      maxVertical = Math.max(maxVertical, vertical + 1);
      maxHorizontal = Math.max(maxHorizontal, horizontal);
    });

    return { vertical: maxVertical, horizontal: maxHorizontal };
  }

  const triggerNode = nodes.find((node) => node.type_id === "4");
  return triggerNode ? getDepth(triggerNode.node_id) : { vertical: 0, horizontal: 0 };
}

export async function hashURL(url) {
  const normalizedURL = decodeURIComponent(url.trim().toLowerCase());
  const encoder = new TextEncoder();
  const data = encoder.encode(normalizedURL);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function calculateMaxDelay(nodes) {
  const nodeMap = new Map(nodes?.map((node) => [node.node_id, node]));
  const root = nodes?.find((node) => node.node_id.startsWith("triggerNode"));

  function convertToDays(amount, unit) {
    const unitMap = { day: 1, week: 7, month: 30, year: 365 };
    return unitMap[unit] ? amount * unitMap[unit] : 0;
  }

  function traverse(node, currentDelay = 0) {
    if (!node) return [];

    let delays = [];

    for (const nextId of node.connector || []) {
      const nextNode = nodeMap.get(nextId);
      if (nextNode) {
        if (nextNode.type_id === "7") {
          // Delay node
          if (nextNode.data.scheduled_day?.includes("-")) {
            // Fixed date
            const fixedDate = new Date(nextNode.data.scheduled_day);
            const today = new Date();
            const daysUntilFixed = Math.ceil((fixedDate - today) / (1000 * 60 * 60 * 24));

            // If current delay already exceeds fixed date, mark as invalid
            if (currentDelay > daysUntilFixed) {
              delays.push("not valid");
              break;
            }

            // Valid branch, continue with fixed date
            delays = delays.concat(traverse(nextNode, daysUntilFixed));
          } else {
            const delay = convertToDays(parseInt(nextNode.data.scheduled_amount, 10), nextNode.data.scheduled_unit);
            delays = delays.concat(traverse(nextNode, currentDelay + delay));
          }
        } else {
          // For non-delay nodes, continue traversing
          delays = delays.concat(traverse(nextNode, currentDelay));
        }
      }
    }

    if (!node.connector || node.connector.length === 0) {
      delays.push(currentDelay);
    }

    return delays;
  }

  const delays = traverse(root);

  if (delays.every((item) => item === "not valid")) {
    return "Completed";
  }

  const validDelays = delays.filter((delay) => delay !== "not valid");
  return validDelays.length > 0 ? formatDuration(Math.max(...validDelays)) : 1;
}

function formatDuration(days) {
  if (days) {
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    const weeks = Math.floor(((days % 365) % 30) / 7);
    const remainingDays = ((days % 365) % 30) % 7;

    return [
      years ? `${years} Year${years > 1 ? "s" : ""}` : "",
      months ? `${months} Month${months > 1 ? "s" : ""}` : "",
      weeks ? `${weeks} Week${weeks > 1 ? "s" : ""}` : "",
      remainingDays ? `${remainingDays} Day${remainingDays > 1 ? "s" : ""}` : ""
    ]
      .filter(Boolean)
      .join(" ");
  }
  return "1 Day";
}

export const handleEmailPaste = (e, callback) => {
  e.preventDefault();

  const pastedText = e.clipboardData.getData("text");

  const match = pastedText.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);

  const emailOnly = match ? match[0] : "";

  const input = e.target;
  const start = input.selectionStart;
  const end = input.selectionEnd;
  const value = input.value;

  const newValue = value.substring(0, start) + emailOnly + value.substring(end);
  input.value = newValue;

  callback(newValue);

  // input.dispatchEvent(new Event('input', { bubbles: true }));
};

export const escapeRegex = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
